// Progressive enhancement for every page: header state, drawer, reveals,
// counters, pointer tilt, sparkle canvases and CTA analytics events.
(() => {
  const doc = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* header */
  const header = document.getElementById('siteHeader');
  const onScroll = () => header && header.classList.toggle('is-scrolled', scrollY > 12);
  addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* mobile drawer */
  const burger = document.getElementById('burger');
  const drawer = document.getElementById('drawer');
  if (burger && drawer) {
    const menuIcon = burger.innerHTML;
    const closeIcon = '<svg class="i" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>';
    const setOpen = (open) => {
      drawer.hidden = !open;
      burger.setAttribute('aria-expanded', String(open));
      burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      burger.innerHTML = open ? closeIcon : menuIcon;
      header.classList.toggle('is-open', open);
    };
    burger.addEventListener('click', () => setOpen(drawer.hidden));
    drawer.addEventListener('click', (e) => e.target.closest('a') && setOpen(false));
    addEventListener('keydown', (e) => e.key === 'Escape' && !drawer.hidden && setOpen(false));
    matchMedia('(min-width: 961px)').addEventListener('change', (e) => e.matches && setOpen(false));
  }

  /* reveal on scroll */
  const reveals = document.querySelectorAll('.reveal');
  if (reduced || !('IntersectionObserver' in window)) {
    reveals.forEach((el) => el.classList.add('is-in'));
  } else {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    reveals.forEach((el) => io.observe(el));
  }

  /* number counters — animate the numeric part, keep the final label intact */
  const counters = document.querySelectorAll('[data-count]');
  if (counters.length && !reduced && 'IntersectionObserver' in window) {
    const run = (el) => {
      const final = el.textContent;
      const m = final.match(/^(\d+)(.*)$/);
      if (!m) return;
      const target = +m[1];
      const rest = m[2];
      const t0 = performance.now();
      const dur = 1600;
      const tick = (t) => {
        const p = Math.min(1, (t - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 4);
        el.textContent = Math.round(target * eased) + rest;
        if (p < 1) requestAnimationFrame(tick);
        else el.textContent = final;
      };
      requestAnimationFrame(tick);
    };
    const cio = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting) { run(e.target); cio.unobserve(e.target); }
    }), { threshold: 0.6 });
    counters.forEach((el) => cio.observe(el));
  }

  /* pointer tilt + shine */
  if (finePointer && !reduced) {
    document.querySelectorAll('.tilt').forEach((el) => {
      const max = el.classList.contains('frame') ? 7 : 5;
      let raf = 0;
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width;
        const y = (e.clientY - r.top) / r.height;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          el.classList.add('is-tilting');
          el.style.setProperty('--ry', ((x - 0.5) * max * 2).toFixed(2) + 'deg');
          el.style.setProperty('--rx', ((0.5 - y) * max * 2).toFixed(2) + 'deg');
          const shine = el.querySelector('.frame-shine') ? el : el.closest('.frame');
          (shine || el).style.setProperty('--mx', (x * 100).toFixed(1) + '%');
          (shine || el).style.setProperty('--my', (y * 100).toFixed(1) + '%');
        });
      });
      el.addEventListener('pointerleave', () => {
        cancelAnimationFrame(raf);
        el.classList.remove('is-tilting');
        el.style.removeProperty('--rx');
        el.style.removeProperty('--ry');
      });
    });
  }

  /* sparkle canvases (pricing + show heroes) */
  document.querySelectorAll('canvas[data-sparkles]').forEach((cv) => {
    const ctx = cv.getContext('2d');
    let w = 0, h = 0, dpr = 1, visible = false, raf = 0;
    const N = innerWidth < 720 ? 45 : 90;
    const pts = [];
    const size = () => {
      dpr = Math.min(devicePixelRatio || 1, 2);
      w = cv.clientWidth; h = cv.clientHeight;
      cv.width = w * dpr; cv.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const seed = () => {
      pts.length = 0;
      for (let i = 0; i < N; i++) pts.push({ x: Math.random() * w, y: Math.random() * h, r: Math.random() * 1.6 + 0.4, s: Math.random() * 0.25 + 0.05, p: Math.random() * Math.PI * 2, gold: Math.random() < 0.45 });
    };
    const star = (x, y, r) => {
      ctx.beginPath();
      ctx.moveTo(x, y - r * 3); ctx.quadraticCurveTo(x, y, x + r * 3, y);
      ctx.quadraticCurveTo(x, y, x, y + r * 3); ctx.quadraticCurveTo(x, y, x - r * 3, y);
      ctx.quadraticCurveTo(x, y, x, y - r * 3); ctx.fill();
    };
    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      for (const p of pts) {
        if (!reduced) { p.y -= p.s; if (p.y < -10) { p.y = h + 10; p.x = Math.random() * w; } }
        const a = 0.35 + 0.65 * Math.abs(Math.sin(t * 0.0012 + p.p));
        ctx.fillStyle = p.gold ? `rgba(255,205,90,${a})` : `rgba(255,255,255,${a * 0.85})`;
        if (p.r > 1.5) star(p.x, p.y, p.r * 0.9);
        else { ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.283); ctx.fill(); }
      }
      if (visible && !reduced) raf = requestAnimationFrame(draw);
    };
    size(); seed(); draw(0);
    new ResizeObserver(() => { size(); seed(); draw(performance.now()); }).observe(cv);
    new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      cancelAnimationFrame(raf);
      if (visible && !reduced) raf = requestAnimationFrame(draw);
    }).observe(cv);
  });

  /* CTA analytics: pushes to GTM's dataLayer (only loaded on the production host) */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-cta]');
    if (!a) return;
    (window.dataLayer = window.dataLayer || []).push({ event: 'cta_click', cta_location: a.dataset.cta, cta_url: a.href });
  });

  /* FAQ: keep only one answer open at a time within a list */
  document.querySelectorAll('.faq-list').forEach((list) => {
    list.addEventListener('toggle', (e) => {
      if (!e.target.open) return;
      list.querySelectorAll('details[open]').forEach((d) => d !== e.target && (d.open = false));
    }, true);
  });

  /* video modal — YouTube (privacy-enhanced embed) or self-hosted MP4, loaded only on demand */
  const vm = document.getElementById('vmodal');
  if (vm && vm.showModal) {
    const frame = vm.querySelector('iframe');
    const vid = vm.querySelector('video');
    const open = (src) => {
      const d = src.dataset;
      if (d.yt) {
        vid.pause(); vid.removeAttribute('src'); vid.hidden = true;
        frame.hidden = false;
        frame.src = `https://www.youtube-nocookie.com/embed/${d.yt}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
      } else {
        frame.src = 'about:blank'; frame.hidden = true;
        vid.hidden = false; vid.poster = d.poster || ''; vid.src = d.file;
        vid.play().catch(() => {});
      }
      vm.querySelectorAll('.vmodal-tabs button').forEach((b) => b.setAttribute('aria-current', String(b.dataset.video === d.video)));
      (window.dataLayer = window.dataLayer || []).push({ event: 'video_open', video_id: d.video });
    };
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-video]');
      if (!t || t.closest('.reel-card')) return; // reel cards play inline
      e.preventDefault();
      if (!vm.open) vm.showModal();
      open(t);
    });
    vm.querySelector('.vmodal-x').addEventListener('click', () => vm.close());
    vm.addEventListener('click', (e) => { if (e.target === vm) vm.close(); }); // backdrop
    vm.addEventListener('close', () => { frame.src = 'about:blank'; vid.pause(); vid.removeAttribute('src'); });
  }

  /* video reel: centre-focused horizontal carousel, the active card plays inline */
  document.querySelectorAll('[data-reel]').forEach((reel) => {
    const track = reel.querySelector('.reel-track');
    const cards = [...track.querySelectorAll('.reel-card')];
    const dots = [...reel.querySelectorAll('[data-go]')];
    let active = -1;
    const left = (c) => c.offsetLeft - (track.clientWidth - c.offsetWidth) / 2;
    const go = (i, smooth = true) => track.scrollTo({ left: left(cards[i]), behavior: smooth && !reduced ? 'smooth' : 'auto' });
    const stop = (c) => {
      c.querySelectorAll('.reel-media video, .reel-media iframe').forEach((el) => el.remove());
      c.classList.remove('is-playing');
    };
    const update = () => {
      const mid = track.scrollLeft + track.clientWidth / 2;
      let best = 0, bd = Infinity;
      cards.forEach((c, i) => { const d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - mid); if (d < bd) { bd = d; best = i; } });
      if (best === active) return;
      if (active >= 0) stop(cards[active]);
      active = best;
      cards.forEach((c, i) => c.classList.toggle('is-active', i === best));
      dots.forEach((d, i) => d.setAttribute('aria-current', String(i === best)));
    };
    const play = (c) => {
      if (c.classList.contains('is-playing')) return;
      const m = c.querySelector('.reel-media');
      let el;
      if (c.dataset.yt) {
        el = document.createElement('iframe');
        el.src = `https://www.youtube-nocookie.com/embed/${c.dataset.yt}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
        el.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        el.allowFullscreen = true;
        el.title = c.querySelector('h3').textContent;
      } else {
        el = document.createElement('video');
        Object.assign(el, { src: c.dataset.file, poster: c.dataset.poster, controls: true, playsInline: true, autoplay: true });
      }
      m.appendChild(el);
      c.classList.add('is-playing');
      if (el.play) el.play().catch(() => {});
      (window.dataLayer = window.dataLayer || []).push({ event: 'video_play', video_id: c.dataset.video });
    };
    let raf = 0;
    track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
    let dragged = false;
    cards.forEach((c, i) => c.addEventListener('click', (e) => {
      if (dragged || e.target.closest('video, iframe')) return;
      if (i !== active) go(i); else play(c);
    }));
    reel.querySelectorAll('[data-dir]').forEach((b) => b.addEventListener('click', () => go(Math.max(0, Math.min(cards.length - 1, active + +b.dataset.dir)))));
    dots.forEach((d, i) => d.addEventListener('click', () => go(i)));
    track.addEventListener('keydown', (e) => {
      const d = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
      if (d) { e.preventDefault(); go(Math.max(0, Math.min(cards.length - 1, active + d))); }
      if (e.key === 'Enter' && active >= 0) play(cards[active]);
    });
    // mouse drag to scroll (touch already scrolls natively)
    let sx = 0, sl = 0, down = false;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.target.closest('video, iframe')) return;
      down = true; dragged = false; sx = e.clientX; sl = track.scrollLeft;
    });
    addEventListener('pointermove', (e) => {
      if (!down) return;
      if (Math.abs(e.clientX - sx) > 6) { dragged = true; track.classList.add('is-dragging'); }
      if (dragged) track.scrollLeft = sl - (e.clientX - sx);
    });
    addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      if (dragged) { track.classList.remove('is-dragging'); update(); go(active); setTimeout(() => (dragged = false), 0); }
    });
    // start on the featured video, centred
    const start = Math.max(0, cards.findIndex((c) => c.classList.contains('is-featured')));
    requestAnimationFrame(() => { go(start, false); update(); });
    addEventListener('resize', () => active >= 0 && go(active, false));
  });

  doc.classList.add('is-ready');
})();
